import { useParams, useNavigate, useNavigationType, useSearchParams, useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSortableTable } from '@/components/shared/SortableTable';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ApplicationRowProcessBadge,
  PositionStatusBadge,
  ScoreBadge,
  ChannelBadge,
  InterviewSubstatusBadges,
  getInterviewSortKey,
} from '@/components/shared/Badges';
import { ArrowLeft, Pencil, Plus, Copy, MoreHorizontal, PowerOff, Clock, UserPlus, UserMinus, FileText, Eye, History, Layers, X, ExternalLink, Power, Sparkles, Send, Loader2, Wand2, Check, Trash2, XCircle, Zap, Square, Archive, ArchiveRestore, Search } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FloatingAiChat } from '@/components/shared/FloatingAiChat';
import IdentityMismatchBadge from '@/components/applications/IdentityMismatchBadge';
import { ListPagination, DEFAULT_LIST_PAGE_SIZE } from '@/components/shared/ListPagination';
import { ListTablePanel } from '@/components/shared/ListTablePanel';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { dateTimeTooltip, formatRelative, formatDate, formatDateTime } from '@/lib/dateUtils';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { statusCellClass } from '@/components/ui/responsive-table/columnLayout';
import {
  jobPositionAiColumnClass,
  jobPositionDialogBodyClass,
  jobPositionDialogContentClass,
  jobPositionFormColumnClass,
  jobPositionFormScrollClass,
} from '@/lib/jobPositionDialogClasses';
import { cn } from '@/lib/utils';
import { activeListFilterClass } from '@/lib/listFilterUi';
import { ClearListFiltersButton } from '@/components/shared/ClearListFiltersButton';
import type { Seniority, ContractType, WorkMode, Application, ApplicationQueueView, ApplicationState, JobPosting } from '@/types';
import { toast } from 'sonner';
import { usePositionDetail } from '@/hooks/usePositionDetail';
import { useCallback, useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import {
  isApplicationAwaitingUpgrade,
  matchesApplicationStatusFilter,
  isApplicationReadyToProcess,
  isApplicationBillingPipelineError,
  getBulkProcessBreakdown,
  bulkProcessableCount,
  canProcessPendingApplications,
  getBulkProcessButtonState,
  reconcileDeferredCountsWithApplications,
} from '@/lib/pendingProcessing';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useTrialWriteGuard } from '@/hooks/useTrialWriteAccess';
import {
  getSelectedProcessableApps,
  useShiftClickSelection,
  SelectAllHeaderCheckbox,
} from '@/lib/bulkProcessApplications';
import { useApplicationProcessQueue } from '@/hooks/useApplicationProcessQueue';
import { usePipelineStatusPoll } from '@/hooks/usePipelineStatusPoll';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';
import { usePersistedListView, useSkipInitialEffect } from '@/hooks/usePersistedListView';
import {
  applyListPageToSearchParams,
  buildListReturnPath,
  isDocumentReload,
  isListViewLandingAfterReload,
  listViewStorageKey,
  loadSessionJson,
  parseListPageParam,
  type ApplicationListReturnState,
  type PositionApplicationsListPersist,
} from '@/lib/listViewPersistence';
import {
  APPLICATIONS_LIST_SORT_VERSION,
  captureQueueSlice,
  createDefaultQueueSlice,
  loadQueueSlicesFromPersist,
} from '@/lib/applicationQueueListState';
import { getApplicationListSortValue } from '@/lib/companyApplicationsList';
import { queryKeys } from '@/lib/queryKeys';
import { ApplicationQueueToggle } from '@/components/applications/ApplicationQueueToggle';
import { shouldShowApplicationsListLoading } from '@/lib/applicationListLoading';
import {
  filterApplicationsForPosition,
  unionPositionApplicationsList,
  useApplicationsQuery,
  usePositionApplicationsFromCache,
} from '@/hooks/useRecruiterListQueries';
import { toBulkArchivePayload } from '@/lib/applicationArchive';
import { APPLICATION_SOURCES } from '@/lib/channels';
import {
  applyBulkArchiveToCache,
  applyBulkUnarchiveToCache,
  scheduleApplicationArchiveInvalidation,
  patchUnlockedDeferredApplications,
} from '@/lib/recruiterCache';
import { usePendingBillingStore } from '@/stores/pendingBillingStore';
import {
  getNextStatusPin,
  getPresentStatusPinCycle,
  buildApplicationDisplayOrder,
} from '@/lib/applicationStateSort';
import ProcessPendingConfirmDialog from '@/components/billing/ProcessPendingConfirmDialog';
import { emitBillingUsageChanged } from '@/lib/billingEvents';
import { ManualApplicationDialog } from '@/components/applications/ManualApplicationDialog';
import { PositionNotificationSettingsSection } from '@/components/positions/PositionNotificationSettingsSection';
import { formatJobLocationForDisplay, normalizeJobLocation } from '@/lib/utils';

function translateEnumLabel(
  t: (key: string) => string,
  prefix: string,
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  const key = `${prefix}.${value}`;
  const translated = t(key);
  return translated === key ? value : translated;
}

const PositionDetailPage = () => {
  const { posId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany } = useAuth();
  const { t } = useLanguage();
  const hook = usePositionDetail(posId);
  // Sprint 3 (S3.F) — local-only modal state for the delete confirmation
  // (mirrors the same two-option flow as the list page).
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);

  if (!hook.position) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  const position = hook.position;
  const activeTab = searchParams.get('tab') || 'overview';
  const isApplicationsTab = activeTab === 'applications';

  const outputLanguage = position.outputLanguage ?? selectedCompany?.defaultOutputLanguage ?? 'it';
  const outputLanguageLabel = outputLanguage === 'it'
    ? t('positions.outputLanguageIt')
    : t('positions.outputLanguageEn');
  const teamName = position.team
    ? hook.companyTeams.find(team => team.id === position.team)?.name ?? position.team
    : undefined;
  const assignedMembers = (position.assignedMembers ?? [])
    .map(uid => hook.allMembers.find(member => member.userId === uid))
    .filter((member): member is NonNullable<typeof member> => Boolean(member));

  return (
    <div
      className={cn(
        isApplicationsTab
          ? 'flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden'
          : 'space-y-6',
      )}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/job-positions')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-semibold truncate">{position.title}</h2>
            {position.versions && position.versions.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium shrink-0">
                v{position.versions[position.versions.length - 1].versionNumber}
              </span>
            )}
            <PositionStatusBadge status={position.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {[
              translateEnumLabel(t, 'apply.seniority', position.seniority),
              translateEnumLabel(t, 'apply.contractType', position.contractType),
              translateEnumLabel(t, 'apply.workMode', position.workMode),
              normalizeJobLocation(position.location),
            ].filter(Boolean).join(' · ') || t('positions.noDetails')}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => {
            const tid = position.team || '';
            const autoIds = tid
              ? hook.allMembers.filter(m => (m.teamIds || []).includes(tid)).map(m => m.userId).filter(Boolean)
              : [];
            hook.setTeamAutoMembers(autoIds);
            hook.setEditData({ ...position });
            hook.setEditOpen(true);
          }}
        >
          <Pencil className="h-3.5 w-3.5" /> {t('common.edit')}
        </Button>
        {/* Sprint 3 (S3.F): consolidated lifecycle actions in a "More"
            dropdown so the header stays tidy. The list page already has
            the same actions on each row; this gives recruiters parity
            without forcing a navigation back to the list. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t('common.moreActions')}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {position.status !== 'cancelled' && (
              <DropdownMenuItem
                onClick={() => { void hook.handleToggleActive(); }}
              >
                {position.status === 'active' ? (
                  <>
                    <PowerOff className="h-3.5 w-3.5 mr-2" />
                    {t('positions.closePosition')}
                  </>
                ) : (
                  <>
                    <Power className="h-3.5 w-3.5 mr-2" />
                    {t('positions.activatePosition')}
                  </>
                )}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => setDeleteOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              {t('common.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs
        value={searchParams.get('tab') || 'overview'}
        onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
        className={cn(isApplicationsTab && 'flex min-h-0 flex-1 flex-col')}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-between gap-4 pb-2',
            !isApplicationsTab && 'sticky top-0 z-10 bg-background',
          )}
        >
          <TabsList>
            <TabsTrigger value="overview">{t('positions.overview')}</TabsTrigger>
            <TabsTrigger value="postings">{t('positions.postings')} ({hook.postings.length})</TabsTrigger>
            <TabsTrigger value="applications">{t('positions.applicationsTab')} ({position.applicationsCount ?? hook.applications.length})</TabsTrigger>
            <TabsTrigger value="notifications">{t('positionNotif.section.title')}</TabsTrigger>
            <TabsTrigger value="history">{t('positions.history')}</TabsTrigger>
            <TabsTrigger value="versions">{t('positions.versions')}</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2 shrink-0">
            {activeTab === 'postings' && (
              <Button size="sm" className="gap-1.5" disabled={!hook.canCreatePosting} title={!hook.canCreatePosting ? t('positions.mustBeActiveToPost') : undefined} onClick={() => hook.setPostingDialog(true)}>
                <Plus className="h-3.5 w-3.5" /> {t('postings.newPosting')}
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  [t('positions.seniority'), translateEnumLabel(t, 'apply.seniority', position.seniority)],
                  [t('positions.contract'), translateEnumLabel(t, 'apply.contractType', position.contractType)],
                  [t('positions.workMode'), translateEnumLabel(t, 'apply.workMode', position.workMode)],
                  [t('positions.location'), formatJobLocationForDisplay(position.location, t('positions.notSpecified'))],
                  [t('positions.salary'), position.salaryMin && position.salaryMax ? `${position.salaryMin.toLocaleString()} – ${position.salaryMax.toLocaleString()} ${position.salaryCurrency}` : undefined],
                  [t('positions.created'), formatDate(position.createdAt)],
                  [t('positions.team'), teamName],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <p className="text-xs text-muted-foreground mb-1">{label as string}</p>
                    <p className="text-sm font-medium">{(value as string) || '—'}</p>
                  </div>
                ))}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('positions.outputLanguage')}</p>
                  <p className="text-sm font-medium">{outputLanguageLabel}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                    {t('positions.overviewLanguageHint')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground mb-3">{t('positions.assignedMembers')}</p>
              {assignedMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('positions.overviewNoAssignedMembers')}</p>
              ) : (
                <ul className="space-y-2">
                  {assignedMembers.map(member => (
                    <li
                      key={member.userId}
                      className="flex items-center gap-2 text-sm rounded-md bg-muted/30 px-3 py-2"
                    >
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-medium text-primary shrink-0">
                        {member.displayName[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{member.displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                      </div>
                      {member.userId === hook.creatorUserId && (
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium shrink-0">
                          {t('positions.assignedMembersCreator')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground mb-3">{t('positions.description')}</p>
              <div className="prose prose-sm max-w-none text-sm text-foreground">
                <ReactMarkdown>{position.description || ''}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="postings" className="mt-4">
          <ListTablePanel showFooter={false}>
          <ResponsiveTable<JobPosting>
                data={hook.postings}
                columns={[
                  {
                    id: 'title',
                    label: t('positions.title'),
                    priority: 'primary',
                    alwaysVisible: true,
                    ...TABLE_COL.title,
                    truncate: true,
                    render: (p) => (
                      <span className="font-medium truncate block" title={p.title || p.positionTitle || '—'}>
                        {p.title || p.positionTitle || '—'}
                      </span>
                    ),
                  },
                  {
                    id: 'channel',
                    label: t('postings.channel'),
                    priority: 'secondary',
                    ...TABLE_COL.channel,
                    render: (p) => <ChannelBadge channel={p.channel} />,
                  },
                  {
                    id: 'status',
                    label: t('positions.status'),
                    priority: 'primary',
                    alwaysVisible: true,
                    ...TABLE_COL.status,
                    render: (p) => (
                      <span className={`inline-flex items-center gap-1.5 text-xs whitespace-nowrap ${p.isActive ? 'text-success' : 'text-muted-foreground'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${p.isActive ? 'bg-success' : 'bg-muted-foreground'}`} />
                        {p.isActive ? t('postings.active') : t('postings.inactive')}
                      </span>
                    ),
                  },
                  {
                    id: 'applicationsCount',
                    label: t('positions.apps'),
                    priority: 'secondary',
                    ...TABLE_COL.count,
                    render: (p) => <span className="font-medium tabular-nums">{p.applicationsCount}</span>,
                  },
                  {
                    id: 'publishedAt',
                    label: t('postings.published'),
                    priority: 'tertiary',
                    hideOn: 'mobile',
                    ...TABLE_COL.date,
                    render: (p) => (
                      <span className="text-muted-foreground">{formatRelative(p.publishedAt)}</span>
                    ),
                  },
                  {
                    id: 'actions',
                    label: '',
                    priority: 'hidden',
                    alwaysVisible: true,
                    ...TABLE_COL.actions,
                    render: (p) => (
                      <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                          {p.isActive && (
                            <DropdownMenuItem onClick={() => { window.open(p.landingPageUrl, '_blank'); }}>
                              <Eye className="h-3.5 w-3.5 mr-2" /> {t('positions.viewPosting')}
                            </DropdownMenuItem>
                          )}
                          {p.isActive && (
                            <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(p.landingPageUrl); toast.success(t('postings.urlCopied')); }}>
                              <Copy className="h-3.5 w-3.5 mr-2" /> {t('positions.copyUrl')}
                            </DropdownMenuItem>
                          )}
                          {p.isActive ? (
                            <DropdownMenuItem onClick={() => hook.handleTogglePosting(p.id)}>
                              <PowerOff className="h-3.5 w-3.5 mr-2" />
                              {t('postings.deactivate')}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => hook.handleTogglePosting(p.id)}>
                              <Power className="h-3.5 w-3.5 mr-2" />
                              {t('postings.activate')}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => { hook.setEditPosting(p); hook.setEditChannel(p.channel ?? 'Other'); hook.setEditTitle(p.title || ''); }}>
                            <Pencil className="h-3.5 w-3.5 mr-2" /> {t('common.edit')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    ),
                  },
                ]}
                getRowKey={(p) => p.id}
                storageKey="position-postings"
                emptyState={<div className="py-12 text-center text-muted-foreground text-sm">{t('postings.noPostings')}</div>}
                ariaLabel="Postings"
              />
          </ListTablePanel>
          <Dialog open={hook.postingDialog} onOpenChange={hook.setPostingDialog}>
            <DialogContent className="max-w-sm">
               <DialogHeader><DialogTitle>{t('postings.newPosting')}</DialogTitle></DialogHeader>
               <div className="space-y-4">
                 <div className="space-y-2">
                   <Label>{t('postings.titleOverride')}</Label>
                   <Input value={hook.newPostingTitle} onChange={e => hook.setNewPostingTitle(e.target.value)} placeholder={position.title} />
                 </div>
                 <div className="space-y-2">
                   <Label>{t('postings.channel')} *</Label>
                  <Select value={hook.newPostingChannel} onValueChange={v => hook.setNewPostingChannel(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {hook.channels.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                 <Button variant="outline" onClick={() => hook.setPostingDialog(false)}>{t('common.cancel')}</Button>
                 <Button onClick={hook.handleCreatePosting}>{t('common.save')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Channel Dialog */}
          <Dialog open={!!hook.editPosting} onOpenChange={() => hook.setEditPosting(null)}>
            <DialogContent className="max-w-sm">
               <DialogHeader><DialogTitle>{t('postings.editPosting')}</DialogTitle></DialogHeader>
               <div className="space-y-4">
                 <div className="space-y-2">
                   <Label>{t('positions.title')}</Label>
                   <Input value={hook.editTitle} onChange={e => hook.setEditTitle(e.target.value)} placeholder={position.title} />
                 </div>
                 <div className="space-y-2">
                   <Label>{t('postings.channel')}</Label>
                  <Select value={hook.editChannel} onValueChange={v => hook.setEditChannel(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {hook.channels.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                 <Button variant="outline" onClick={() => hook.setEditPosting(null)}>{t('common.cancel')}</Button>
                 <Button onClick={hook.handleSaveEditPosting}>{t('common.save')}</Button>
               </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent
          value="applications"
          className="mt-4 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <ApplicationsSubTable
            applications={hook.applications}
            applicationsLoading={hook.applicationsLoading}
            navigate={navigate}
            posId={position.id}
            companyId={selectedCompany?.id}
            positionTitle={position.title}
            teamName={
              position.team
                ? hook.companyTeams.find((ct) => ct.id === position.team)?.name ?? position.team
                : t('positions.noTeam')
            }
            onRefetch={hook.refetchApplications}
            onPatchApplications={hook.patchApplications}
            onAddApplication={() => setManualDialogOpen(true)}
          />
        </TabsContent>

        {/* Notifications Tab — v3-notif FASE 1 (27/05/26) */}
        <TabsContent value="notifications" className="mt-4">
          {selectedCompany?.id && position.id && (
            <PositionNotificationSettingsSection
              companyId={selectedCompany.id}
              positionId={position.id}
            />
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="border-border/50">
            <CardContent className="pt-6">
              {(position.history && position.history.length > 0) ? (
                <div className="relative">
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border/50" />
                  <div className="space-y-4">
                    {[...position.history].reverse().map(entry => {
                      const eventConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
                        created: { icon: <Plus className="h-3 w-3" />, label: 'Created', color: 'bg-success/20 text-success' },
                        updated: { icon: <Pencil className="h-3 w-3" />, label: 'Updated', color: 'bg-primary/20 text-primary' },
                        status_changed: { icon: <Power className="h-3 w-3" />, label: 'Status Changed', color: 'bg-warning/20 text-warning' },
                        member_added: { icon: <UserPlus className="h-3 w-3" />, label: 'Member Added', color: 'bg-info/20 text-info' },
                        member_removed: { icon: <UserMinus className="h-3 w-3" />, label: 'Member Removed', color: 'bg-muted text-muted-foreground' },
                        posting_created: { icon: <FileText className="h-3 w-3" />, label: 'Posting Created', color: 'bg-accent text-accent-foreground' },
                        posting_toggled: { icon: <Power className="h-3 w-3" />, label: 'Posting Toggled', color: 'bg-muted text-muted-foreground' },
                      };
                      const config = eventConfig[entry.event] || { icon: <Clock className="h-3 w-3" />, label: entry.event, color: 'bg-muted text-muted-foreground' };
                      return (
                        <div key={entry.id} className="flex items-start gap-3 relative pl-1">
                          <div className={`h-[30px] w-[30px] rounded-full flex items-center justify-center shrink-0 z-10 ${config.color}`}>
                            {config.icon}
                          </div>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium">{config.label}</span>
                              {entry.versionNumber && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">v{entry.versionNumber}</span>
                              )}
                            </div>
                            {entry.details && <p className="text-xs text-muted-foreground mt-0.5">{entry.details}</p>}
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {entry.triggeredByName} · {formatDateTime(entry.timestamp)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">{t('positions.noHistory')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Versions Tab */}
        <TabsContent value="versions" className="mt-4">
          <Card className="border-border/50">
            <CardContent className="pt-6">
              {(position.versions && position.versions.length > 0) ? (
                <div className="space-y-3">
                  {[...position.versions].reverse().map((ver, idx) => {
                    const isLatest = idx === 0;
                    return (
                      <div key={ver.versionNumber} className={`flex items-center justify-between p-3 rounded-lg border ${isLatest ? 'border-primary/30 bg-primary/5' : 'border-border/50'}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`h-8 w-8 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${isLatest ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                            v{ver.versionNumber}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">{ver.changesSummary}</span>
                              {isLatest && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium shrink-0">{t('positions.current')}</span>}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {ver.createdByName} · {formatDateTime(ver.createdAt)}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-xs shrink-0"
                          onClick={() => { hook.setSelectedVersion(ver); hook.setVersionViewOpen(true); }}
                        >
                          <Eye className="h-3.5 w-3.5" /> {t('positions.view')}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">{t('positions.noVersions')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Version View Dialog */}
      <Dialog open={hook.versionViewOpen} onOpenChange={hook.setVersionViewOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
             <DialogTitle className="flex items-center gap-2">
               {t('positions.version')} {hook.selectedVersion?.versionNumber}
               {hook.selectedVersion && position.versions && hook.selectedVersion.versionNumber === position.versions[position.versions.length - 1]?.versionNumber && (
                 <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{t('positions.current')}</span>
              )}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {hook.selectedVersion?.createdByName} · {hook.selectedVersion && formatDateTime(hook.selectedVersion.createdAt)}
            </p>
            <p className="text-xs text-muted-foreground">{hook.selectedVersion?.changesSummary}</p>
          </DialogHeader>
          {hook.selectedVersion && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                 {[
                   [t('positions.title'), hook.selectedVersion.snapshot.title],
                   [t('positions.seniority'), hook.selectedVersion.snapshot.seniority],
                   [t('positions.contract'), hook.selectedVersion.snapshot.contractType],
                   [t('positions.workMode'), hook.selectedVersion.snapshot.workMode],
                   [t('positions.location'), formatJobLocationForDisplay(hook.selectedVersion.snapshot.location, t('positions.notSpecified'))],
                   [t('positions.salary'), hook.selectedVersion.snapshot.salaryMin && hook.selectedVersion.snapshot.salaryMax ? `${hook.selectedVersion.snapshot.salaryMin.toLocaleString()} – ${hook.selectedVersion.snapshot.salaryMax.toLocaleString()} ${hook.selectedVersion.snapshot.salaryCurrency}` : undefined],
                   [t('positions.team'), hook.selectedVersion.snapshot.team],
                   [t('positions.status'), hook.selectedVersion.snapshot.status],
                  ['Assigned Members', hook.selectedVersion.snapshot.assignedMembers?.length ? `${hook.selectedVersion.snapshot.assignedMembers.length} member(s)` : undefined],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <p className="text-xs text-muted-foreground mb-0.5">{label as string}</p>
                    <p className="text-sm font-medium">{(value as string) || '—'}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                 <p className="text-xs text-muted-foreground">{t('positions.options')}</p>
                 <div className="flex flex-wrap gap-2">
                   {hook.selectedVersion.snapshot.requirePhone && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t('positions.requirePhone')}</span>}
                   {hook.selectedVersion.snapshot.requireLinkedIn && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t('positions.requireLinkedIn')}</span>}
                   {hook.selectedVersion.snapshot.notifyCandidate && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t('positions.notifyCandidate')}</span>}
                   {hook.selectedVersion.snapshot.allowDuplicateApplications && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t('positions.allowDuplicateApplications')}</span>}
                   {!hook.selectedVersion.snapshot.requirePhone && !hook.selectedVersion.snapshot.requireLinkedIn && !hook.selectedVersion.snapshot.notifyCandidate && !hook.selectedVersion.snapshot.allowDuplicateApplications && <span className="text-sm text-muted-foreground">—</span>}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('positions.description')}</p>
                <div className="prose prose-sm max-w-none text-sm text-foreground bg-muted/20 rounded-lg p-3 max-h-60 overflow-y-auto">
                  <ReactMarkdown>{hook.selectedVersion.snapshot.description || ''}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog open={hook.editOpen} onOpenChange={(open) => { hook.setEditOpen(open); if (!open) hook.setAiChatOpen(false); }}>
        <DialogContent className={jobPositionDialogContentClass({ aiChatOpen: hook.aiChatOpen })}>
          <div className={jobPositionDialogBodyClass}>
            {/* Form side — hidden on mobile when AI chat is open */}
            <div className={jobPositionFormColumnClass(hook.aiChatOpen)}>
              {/* Header */}
              <div className="shrink-0 flex items-center justify-between px-6 pt-6 pb-4 border-b border-border/40">
                <DialogTitle className="text-base">{t('positions.editPosition')}</DialogTitle>
                <div className="flex items-center gap-2 pr-6">
                  <Button
                    variant={hook.aiChatOpen ? 'default' : 'outline'}
                    size="sm"
                    className="gap-1.5 text-xs h-8"
                    onClick={() => hook.setAiChatOpen(!hook.aiChatOpen)}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {hook.aiChatOpen ? t('positions.closeAI') : t('positions.aiAssistant')}
                  </Button>
                </div>
              </div>
              {/* Scrollable form */}
              <div className={jobPositionFormScrollClass}>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                     <Label className="text-xs font-medium">{t('positions.title')} *</Label>
                     <Input value={hook.editData.title || ''} onChange={e => hook.setEditData(d => ({ ...d, title: e.target.value }))} className="h-9" />
                   </div>
                   <div className="space-y-1.5">
                     <Label className="text-xs font-medium">{t('positions.description')} *</Label>
                     {/* Sprint 3 (S3.D): unified WYSIWYG editor — same
                         component as the create dialog so the recruiter
                         doesn't see two different editing experiences for
                         the same field. */}
                     <MarkdownEditor
                       value={hook.editData.description || ''}
                       onChange={(next) => hook.setEditData(d => ({ ...d, description: next }))}
                       ariaLabel={t('positions.description')}
                     />
                   </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.seniority')}</Label>
                      {hook.editData.seniority !== undefined && !['Junior', 'Junior-Mid', 'Mid', 'Mid-Senior', 'Senior', '', undefined].includes(hook.editData.seniority) || hook.editData.seniority === '__other__' ? (
                        <div className="flex gap-1.5">
                          <Input autoFocus value={hook.editData.seniority === '__other__' ? '' : (hook.editData.seniority || '')} onChange={e => hook.setEditData(d => ({ ...d, seniority: e.target.value || '__other__' }))} className="h-9" placeholder="Enter custom seniority..." />
                          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => hook.setEditData(d => ({ ...d, seniority: undefined }))}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Select value={hook.editData.seniority || ''} onValueChange={v => hook.setEditData(d => ({ ...d, seniority: v === '__other__' ? '__other__' : v as Seniority }))}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            {['Junior', 'Junior-Mid', 'Mid', 'Mid-Senior', 'Senior'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            <SelectItem value="__other__">Other...</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.contractType')}</Label>
                      {hook.editData.contractType !== undefined && !['Full-time', 'Part-time', 'Freelance', 'Internship', 'Temporary', '', undefined].includes(hook.editData.contractType) || hook.editData.contractType === '__other__' ? (
                        <div className="flex gap-1.5">
                          <Input autoFocus value={hook.editData.contractType === '__other__' ? '' : (hook.editData.contractType || '')} onChange={e => hook.setEditData(d => ({ ...d, contractType: e.target.value || '__other__' }))} className="h-9" placeholder="Enter custom contract type..." />
                          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => hook.setEditData(d => ({ ...d, contractType: undefined }))}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Select value={hook.editData.contractType || ''} onValueChange={v => hook.setEditData(d => ({ ...d, contractType: v === '__other__' ? '__other__' : v as ContractType }))}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            {['Full-time', 'Part-time', 'Freelance', 'Internship', 'Temporary'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            <SelectItem value="__other__">Other...</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.workMode')}</Label>
                      <Select value={hook.editData.workMode || ''} onValueChange={v => hook.setEditData(d => ({ ...d, workMode: v as WorkMode }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {['On-site', 'Hybrid', 'Remote'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.location')}</Label>
                      <Select value={hook.editData.location || ''} onValueChange={v => hook.setEditData(d => ({ ...d, location: v }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select location..." /></SelectTrigger>
                        <SelectContent>
                          {hook.companyOffices.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Salary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                       <Label className="text-xs font-medium">{t('positions.salaryMin')}</Label>
                     <Input type="number" value={hook.editData.salaryMin || ''} onChange={e => hook.setEditData(d => ({ ...d, salaryMin: Number(e.target.value) }))} className="h-9" />
                   </div>
                   <div className="space-y-1.5">
                       <Label className="text-xs font-medium">{t('positions.salaryMax')}</Label>
                     <Input type="number" value={hook.editData.salaryMax || ''} onChange={e => hook.setEditData(d => ({ ...d, salaryMax: Number(e.target.value) }))} className="h-9" />
                   </div>
                   <div className="space-y-1.5">
                       <Label className="text-xs font-medium">{t('positions.currency')}</Label>
                      <Select value={hook.editData.salaryCurrency || 'EUR'} onValueChange={v => hook.setEditData(d => ({ ...d, salaryCurrency: v }))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Candidate locale — screening/reports, emails, public posting. */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.outputLanguage')}</Label>
                    <Select
                      value={hook.editData.outputLanguage ?? 'it'}
                      onValueChange={v =>
                        hook.setEditData(d => ({ ...d, outputLanguage: v as 'it' | 'en' }))
                      }
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="it">{t('positions.outputLanguageIt')}</SelectItem>
                        <SelectItem value="en">{t('positions.outputLanguageEn')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      {t('positions.outputLanguageHint')}
                    </p>
                  </div>
                  {/* Switches */}
                  <div className="space-y-2.5 pt-1">
                    <div className="flex items-center justify-between">
                       <Label className="text-xs">{t('positions.requirePhone')}</Label>
                       <Switch checked={hook.editData.requirePhone} onCheckedChange={v => hook.setEditData(d => ({ ...d, requirePhone: v }))} />
                     </div>
                     <div className="flex items-center justify-between">
                       <Label className="text-xs">{t('positions.requireLinkedIn')}</Label>
                       <Switch checked={hook.editData.requireLinkedIn} onCheckedChange={v => hook.setEditData(d => ({ ...d, requireLinkedIn: v }))} />
                     </div>
                     {/* Notify candidate toggle — hidden for now, hardcoded to false
                     <div className="flex items-center justify-between">
                       <Label className="text-xs">{t('positions.notifyCandidate')}</Label>
                       <Switch checked={hook.editData.notifyCandidate} onCheckedChange={v => hook.setEditData(d => ({ ...d, notifyCandidate: v }))} />
                     </div>
                     */}
                     <div className="flex items-center justify-between">
                       <div className="flex flex-col gap-0.5">
                         <Label className="text-xs">{t('positions.allowDuplicateApplications')}</Label>
                         <p className="text-[11px] text-muted-foreground">{t('positions.allowDuplicateApplicationsHint')}</p>
                       </div>
                       <Switch checked={hook.editData.allowDuplicateApplications} onCheckedChange={v => hook.setEditData(d => ({ ...d, allowDuplicateApplications: v }))} />
                     </div>
                   </div>
                   {/* Team with auto-assign */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.team')}</Label>
                    <Select value={hook.editData.team || ''} onValueChange={v => {
                      const newTeamMemberIds = hook.allMembers.filter(m => m.teamIds?.includes(v)).map(m => m.userId);
                      const currentIds = hook.editData.assignedMembers || [];
                      const manualIds = currentIds.filter(id => !hook.teamAutoMembers.includes(id) || id === hook.creatorUserId);
                      const newIds = [...new Set([...manualIds, ...newTeamMemberIds])];
                      hook.setTeamAutoMembers(newTeamMemberIds);
                      hook.setEditData(d => ({ ...d, team: v, assignedMembers: newIds }));
                    }}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Select team..." /></SelectTrigger>
                      <SelectContent>
                        {hook.companyTeams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Assigned Members */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.assignedMembers')}</Label>
                    <div className="space-y-1.5">
                      {(hook.editData.assignedMembers || []).map(uid => {
                        const m = hook.allMembers.find(x => x.userId === uid);
                        if (!m) return null;
                        const isCreator = uid === hook.creatorUserId;
                        return (
                          <div key={uid} className="flex items-center justify-between bg-muted/30 rounded-md px-2.5 py-1.5">
                            <div className="flex items-center gap-2">
                              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">{m.displayName[0]}</div>
                              <span className="text-xs font-medium">{m.displayName}</span>
                              <span className="text-[10px] text-muted-foreground">{m.email}</span>
                              <span className="text-[10px] text-muted-foreground">{m.jobRole?.replace('_', ' ')}</span>
                              {m.teamIds?.[0] && <span className="text-[10px] text-muted-foreground">· {m.teamIds[0]}</span>}
                              {isCreator && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">Creator</span>}
                            </div>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => hook.setEditData(d => ({ ...d, assignedMembers: (d.assignedMembers || []).filter(id => id !== uid) }))}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                      {(() => {
                        const available = hook.allMembers.filter(m => !(hook.editData.assignedMembers || []).includes(m.userId));
                        if (available.length === 0) return <p className="text-[11px] text-muted-foreground">All members are assigned</p>;
                        return (
                          <div className="relative">
                            <Input
                              value={hook.memberSearch}
                              onChange={e => hook.setMemberSearch(e.target.value)}
                              placeholder="Search by name or email..."
                              className="h-8 text-xs"
                            />
                            {hook.memberSearch.trim() && (
                              <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-md shadow-md max-h-32 overflow-y-auto">
                                {available.filter(m =>
                                  m.displayName.toLowerCase().includes(hook.memberSearch.toLowerCase()) ||
                                  m.email.toLowerCase().includes(hook.memberSearch.toLowerCase())
                                ).map(m => (
                                  <button
                                    key={m.userId}
                                    className="flex items-center gap-2 w-full text-left px-2.5 py-1.5 text-xs hover:bg-accent/50 transition-colors"
                                    onClick={() => {
                                      hook.setEditData(d => ({ ...d, assignedMembers: [...(d.assignedMembers || []), m.userId] }));
                                      hook.setMemberSearch('');
                                    }}
                                  >
                                    <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-medium text-primary">{m.displayName[0]}</div>
                                    <span className="font-medium">{m.displayName}</span>
                                    <span className="text-muted-foreground">{m.email}</span>
                                    {m.teamIds?.[0] && <span className="text-muted-foreground ml-auto">{m.teamIds[0]}</span>}
                                  </button>
                                ))}
                                {available.filter(m =>
                                  m.displayName.toLowerCase().includes(hook.memberSearch.toLowerCase()) ||
                                  m.email.toLowerCase().includes(hook.memberSearch.toLowerCase())
                                ).length === 0 && (
                                  <p className="px-2.5 py-2 text-xs text-muted-foreground">No members found</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className="shrink-0 flex justify-end gap-2 px-6 py-3 border-t border-border/40 bg-muted/20">
                <Button variant="outline" size="sm" onClick={() => hook.setEditOpen(false)}>{t('common.cancel')}</Button>
                <Button size="sm" onClick={hook.handleSavePosition} disabled={hook.saving}>{hook.saving ? 'Saving...' : t('common.save')}</Button>
              </div>
            </div>

            {/* AI Assistant side — full screen on mobile; form returns when closed */}
            {hook.aiChatOpen && (
              <div className={jobPositionAiColumnClass}>
                {/* AI Header */}
                <div className="shrink-0 px-4 py-3 border-b border-border/40 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-medium truncate">AI Assistant</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 md:hidden"
                    onClick={() => hook.setAiChatOpen(false)}
                    aria-label={t('positions.closeAI')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Messages */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3">
                  {hook.aiMessages.length === 0 && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-muted-foreground font-medium">Try asking:</p>
                      {[
                        { icon: '✨', text: 'Improve the current description' },
                        { icon: '🔄', text: 'Rewrite for a different seniority level' },
                        { icon: '💰', text: 'Suggest a competitive salary range' },
                        { icon: '📋', text: 'Add missing requirements or skills' },
                      ].map(prompt => (
                        <button
                          key={prompt.text}
                          className="flex items-center gap-2 w-full text-left text-xs p-2.5 rounded-lg border border-border/50 bg-background hover:bg-accent/50 hover:border-border transition-all"
                          onClick={() => { hook.setAiInput(prompt.text); }}
                        >
                          <span>{prompt.icon}</span>
                          <span>{prompt.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {hook.aiMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap'
                          : 'bg-background border border-border/50 rounded-bl-sm'
                      }`}>
                        {msg.role === 'user' ? (
                          msg.text
                        ) : (
                          <div className="prose prose-xs dark:prose-invert max-w-none
                              prose-headings:font-semibold prose-headings:text-xs prose-headings:mt-2 prose-headings:mb-1
                              prose-p:my-1 prose-p:text-xs prose-p:leading-relaxed
                              prose-ul:my-1 prose-ul:pl-3 prose-li:text-xs prose-li:my-0
                              prose-ol:my-1 prose-ol:pl-3
                              prose-strong:font-semibold
                              prose-code:text-[10px] prose-code:bg-muted prose-code:px-1 prose-code:rounded
                              prose-hr:my-2">
                            <ReactMarkdown>
                              {msg.text}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {hook.aiLoading && (
                    <div className="flex justify-start">
                      <div className="bg-background border border-border/50 rounded-xl rounded-bl-sm px-3 py-2">
                        <span className="flex gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Suggestions Preview Panel */}
                {hook.autoFillSuggestions && (
                  <div className="mx-3 mb-2 rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        AI Suggestions
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-muted-foreground"
                        onClick={hook.dismissAutoFillSuggestions}
                      >
                        Dismiss
                      </Button>
                    </div>
                    <div className="space-y-1 text-xs">
                      {hook.autoFillSuggestions.title && (
                        <div><span className="text-muted-foreground">Title:</span> {hook.autoFillSuggestions.title}</div>
                      )}
                      {hook.autoFillSuggestions.seniority && (
                        <div><span className="text-muted-foreground">Seniority:</span> {hook.autoFillSuggestions.seniority}</div>
                      )}
                      {hook.autoFillSuggestions.contractType && (
                        <div><span className="text-muted-foreground">Contract:</span> {hook.autoFillSuggestions.contractType}</div>
                      )}
                      {hook.autoFillSuggestions.workMode && (
                        <div><span className="text-muted-foreground">Work Mode:</span> {hook.autoFillSuggestions.workMode}</div>
                      )}
                      {normalizeJobLocation(hook.autoFillSuggestions.location) && (
                        <div><span className="text-muted-foreground">Location:</span> {normalizeJobLocation(hook.autoFillSuggestions.location)}</div>
                      )}
                      {(hook.autoFillSuggestions.salaryMin || hook.autoFillSuggestions.salaryMax) && (
                        <div>
                          <span className="text-muted-foreground">Salary:</span>{' '}
                          {hook.autoFillSuggestions.salaryMin && hook.autoFillSuggestions.salaryMax
                            ? `${hook.autoFillSuggestions.salaryMin.toLocaleString()} – ${hook.autoFillSuggestions.salaryMax.toLocaleString()}`
                            : hook.autoFillSuggestions.salaryMin?.toLocaleString() ?? hook.autoFillSuggestions.salaryMax?.toLocaleString()
                          }
                          {hook.autoFillSuggestions.salaryCurrency && ` ${hook.autoFillSuggestions.salaryCurrency}`}
                        </div>
                      )}
                      {hook.autoFillSuggestions.description && (
                        <div>
                          <div className="text-muted-foreground mb-0.5">Description:</div>
                          <div className="prose prose-xs max-w-none max-h-32 overflow-y-auto rounded border border-border/50 bg-background/50 p-1.5 text-xs leading-relaxed">
                            <ReactMarkdown>{hook.autoFillSuggestions.description}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="w-full gap-1"
                      onClick={hook.applyAutoFillSuggestions}
                    >
                      <Check className="h-3 w-3" />
                      Apply Suggestions
                    </Button>
                  </div>
                )}

                {/* Input */}
                <div className="shrink-0 p-3 border-t border-border/40 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                  {/* Auto-Fill button */}
                  <Button
                    variant={hook.autoFillReady ? "default" : "outline"}
                    size="sm"
                    className="w-full gap-2"
                    onClick={hook.triggerAutoFill}
                    disabled={hook.autoFillLoading}
                    title={hook.autoFillReady
                      ? "AI is ready to fill your form — click to apply suggestions"
                      : "Chat with the AI to generate field suggestions"}
                  >
                    {hook.autoFillLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    {hook.autoFillLoading ? "Generating suggestions..." : "Auto-Fill Form"}
                  </Button>

                  <div className="flex gap-2 items-center bg-background rounded-lg border border-border/50 px-3 py-1.5 focus-within:ring-1 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all">
                    <textarea
                      className="flex-1 text-xs bg-transparent border-0 outline-none placeholder:text-muted-foreground/60 resize-none min-h-[24px] max-h-[96px] py-0.5"
                      placeholder="Ask AI for help..."
                      value={hook.aiInput}
                      onChange={e => hook.setAiInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          hook.sendAiMessage();
                        }
                      }}
                      rows={1}
                    />
                    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary" onClick={hook.sendAiMessage} disabled={hook.aiLoading}>
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sprint 3 (S3.F) — delete confirmation. Two options mirror the
          list page: soft-cancel (recoverable) vs permanent delete. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md max-w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-center">{t('positions.deletePosition')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-center">{t('positions.deleteConfirm')}</p>
          <div className="space-y-2 mt-1">
            <div className="rounded-md border border-border/50 p-3">
              <p className="text-xs font-medium mb-0.5">{t('positions.softDeleteTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('positions.softDeleteDesc')}</p>
            </div>
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-xs font-medium text-destructive mb-0.5">{t('positions.hardDeleteTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('positions.hardDeleteDesc')}</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const ok = await hook.handleSoftDelete();
                setDeleteOpen(false);
                if (ok) navigate('/job-positions');
              }}
            >
              {t('positions.softDeleteAction')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={async () => {
                const ok = await hook.handlePermanentDelete();
                setDeleteOpen(false);
                if (ok) navigate('/job-positions');
              }}
            >
              {t('positions.hardDeleteAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManualApplicationDialog
        open={manualDialogOpen}
        onOpenChange={setManualDialogOpen}
        companyId={selectedCompany?.id ?? ''}
        positions={[position]}
        defaultPositionId={position.id}
        postings={hook.postings}
        lockPosition
        onSubmitted={({ applicationId, positionId }) => {
          void hook.appendManualApplication(positionId, applicationId);
        }}
      />

      {/* Floating AI Chat — sprint ai_assistant_scope_fix (26/05/26).
          Now backed by applications_assistant (candidate-aware) with
          currentPosition context — handles both candidate queries and
          JD review/improvement requests. Tutte le copy passano da i18n
          (aiChat.positionDetail.*) → l'assistente parla nella lingua
          dell'utente. Il BE già localizza la risposta del modello via
          users.preferredLanguage → languageInstruction nel prompt. */}
      <FloatingAiChat
        open={hook.floatingChatOpen}
        onOpenChange={hook.setFloatingChatOpen}
        expanded={hook.floatingChatExpanded}
        onExpandedChange={hook.setFloatingChatExpanded}
        messages={hook.floatingChatMessages}
        loading={hook.floatingChatLoading}
        input={hook.floatingChatInput}
        onInputChange={hook.setFloatingChatInput}
        onSend={(prompt) => hook.sendFloatingChat(prompt)}
        onReset={() => hook.resetFloatingChat()}
        contextLabel={t('aiChat.positionDetail.contextLabel', { title: position.title })}
        placeholder={t('aiChat.positionDetail.placeholder')}
        suggestedPrompts={[
          t('aiChat.positionDetail.prompt1'),
          t('aiChat.positionDetail.prompt2'),
          t('aiChat.positionDetail.prompt3'),
          t('aiChat.positionDetail.prompt4'),
        ]}
      />

    </div>
  );
};

/* Sub-table for applications tab */
type ApplicationRow = Application & { teamName?: string };

const ApplicationsSubTable = ({
  applications,
  applicationsLoading,
  navigate,
  posId,
  companyId,
  positionTitle,
  teamName,
  onRefetch,
  onPatchApplications,
  onAddApplication,
}: {
  applications: Application[];
  applicationsLoading: boolean;
  navigate: any;
  posId?: string;
  companyId?: string;
  positionTitle?: string;
  teamName?: string;
  onRefetch?: () => Promise<void> | void;
  onPatchApplications?: (updater: (prev: Application[]) => Application[]) => void;
  onAddApplication?: () => void;
}) => {
  const { t } = useLanguage();
  const navigationType = useNavigationType();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const { guardWrite, writeBlocked } = useTrialWriteGuard();
  const setDeferredCounts = usePendingBillingStore(s => s.setDeferredCounts);

  const listPersistKey = companyId && posId
    ? listViewStorageKey(`position-applications:${posId}`, companyId)
    : undefined;
  const pageReload = isDocumentReload();
  const landingAfterReload = pageReload || isListViewLandingAfterReload(navigationType);
  const savedList = listPersistKey && !landingAfterReload
    ? loadSessionJson<PositionApplicationsListPersist>(listPersistKey)
    : null;

  const queueSlicesRef = useRef(
    loadQueueSlicesFromPersist(savedList, savedList?.queueView, { forceDefaultSort: pageReload }),
  );
  const initialQueue = queueSlicesRef.current.queueView;
  const initialSlice = queueSlicesRef.current[initialQueue];

  const [queueView, setQueueView] = useState<ApplicationQueueView>(initialQueue);
  const [statusFilter, setStatusFilter] = useState(
    () => savedList?.statusFilter ?? 'all',
  );
  const [sourceFilter, setSourceFilter] = useState(
    () => savedList?.sourceFilter ?? 'all',
  );
  const [search, setSearch] = useState(() => savedList?.search ?? '');
  const isArchiveView = queueView === 'archive';
  const archivedPositionQuery = usePositionApplicationsFromCache(companyId, posId, {
    archived: true,
    enabled: !!companyId && !!posId,
    positionTitle,
  });
  const positionArchivedCount = archivedPositionQuery.applications.length;
  const activeCompanyAppsQuery = useApplicationsQuery(companyId, false);
  const companyActiveForPosition = useMemo(
    () => filterApplicationsForPosition(
      activeCompanyAppsQuery.data,
      posId,
      false,
      positionTitle,
    ) ?? [],
    [activeCompanyAppsQuery.data, posId, positionTitle],
  );
  const positionActiveCount = useMemo(
    () => unionPositionApplicationsList(applications, companyActiveForPosition).length,
    [applications, companyActiveForPosition],
  );
  const listApplications = isArchiveView
    ? archivedPositionQuery.applications
    : unionPositionApplicationsList(applications, companyActiveForPosition);

  const listLoading = shouldShowApplicationsListLoading({
    enabled: !!companyId && !!posId,
    rowCount: listApplications.length,
    isPending:
      applicationsLoading
      || (isArchiveView && archivedPositionQuery.isLoading),
    isFetching:
      activeCompanyAppsQuery.isFetching
      || archivedPositionQuery.isFetching,
    additionalFetching:
      !isArchiveView
      && listApplications.length === 0
      && (activeCompanyAppsQuery.isLoading || activeCompanyAppsQuery.isFetching),
  });

  const [pinnedStatus, setPinnedStatus] = useState<ApplicationState | null>(
    () => initialSlice.pinnedStatus,
  );
  const [page, setPage] = useState(() => {
    if (!landingAfterReload && searchParams.get('page')) {
      return parseListPageParam(searchParams.get('page'));
    }
    return initialSlice.page;
  });
  const [queueSortDefaults, setQueueSortDefaults] = useState(() => {
    if (pageReload) {
      const fresh = createDefaultQueueSlice();
      return { column: fresh.sortColumn, direction: fresh.sortDirection };
    }
    return {
      column: initialSlice.sortColumn,
      direction: initialSlice.sortDirection,
    };
  });

  useLayoutEffect(() => {
    if (!pageReload || !companyId) return;
    queryClient.removeQueries({ queryKey: ['applications-list', companyId] });
    if (posId) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.positionApplications(companyId, posId, false),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.positionApplications(companyId, posId, true),
      });
    }
  }, [pageReload, companyId, posId, queryClient]);

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
    setSearchParams((prev) => applyListPageToSearchParams(prev, next), { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (landingAfterReload || !searchParams.has('page')) return;
    const urlPage = parseListPageParam(searchParams.get('page'));
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync page from URL only
  }, [searchParams, landingAfterReload]);

  const openApplicationDetail = useCallback((app: ApplicationRow) => {
    if (!posId) return;
    const listReturnPath = buildListReturnPath(location.pathname, searchParams, page);
    navigate(`/job-positions/${posId}/applications/${app.id}`, {
      state: { listReturnPath } satisfies ApplicationListReturnState,
    });
  }, [navigate, location.pathname, searchParams, page, posId]);

  const refetchApplications = useCallback(async () => {
    if (onRefetch) await onRefetch();
  }, [onRefetch]);

  const processQueue = useApplicationProcessQueue({
    companyId,
    applications: listApplications,
    refetchApplications,
    onPatchApplications,
    t,
  });

  usePipelineStatusPoll(
    companyId,
    listApplications,
    !!companyId && !!posId && !isArchiveView,
  );
  const bulkBannerActive = useBulkProcessBannerStore((s) => s.phase === 'active');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSlice.selectedIds ?? []),
  );

  const statusOptions = useMemo(() => [
    { value: 'all', label: t('applications.allStatus') },
    { value: 'APPLIED_NEW', label: t('applications.new') },
    { value: 'PENDING_UPGRADE', label: t('application.pendingUpgrade.badge') },
    { value: 'READY_TO_PROCESS', label: t('application.readyToProcess.badge') },
    { value: 'PIPELINE_RUNNING', label: t('applications.processing') },
    { value: 'PIPELINE_STUCK', label: t('applications.processingStuck') },
    { value: 'AWAITING_DECISION', label: t('applications.awaitingDecision') },
    { value: 'REJECTED', label: t('applications.rejected') },
    { value: 'HIRED', label: t('applications.hired') },
    { value: 'OFFER_DECLINED', label: t('applications.offerDeclined') },
    { value: 'PIPELINE_ERROR', label: t('applications.reviewRequired') },
    { value: 'WITHDRAWN', label: t('applications.withdrawn') },
  ], [t]);

  const filtered = useMemo(() => {
    return listApplications.filter((a) => {
      if (!matchesApplicationStatusFilter(
        a,
        statusFilter,
        hasProcessingCredits,
        { isRowProcessing: processQueue.isInProcessingFilter },
      )) {
        return false;
      }
      if (sourceFilter !== 'all' && a.channel !== sourceFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const name = `${a.candidateFirstName} ${a.candidateLastName}`.toLowerCase();
        const pos = (a.positionTitle ?? positionTitle ?? '').toLowerCase();
        if (!name.includes(q) && !pos.includes(q)) return false;
      }
      return true;
    });
  }, [listApplications, statusFilter, sourceFilter, hasProcessingCredits, search, positionTitle, processQueue.isInProcessingFilter]);

  const hasActiveListFilters = statusFilter !== 'all' || sourceFilter !== 'all' || search.trim().length > 0;

  const clearAllListFilters = useCallback(() => {
    setStatusFilter('all');
    setSourceFilter('all');
    setSearch('');
    setPage(1);
  }, []);

  const filtersHideAllRows =
    listApplications.length > 0
    && filtered.length === 0
    && hasActiveListFilters;

  const rowsWithTeam = useMemo(
    () => filtered.map((app) => ({
      ...app,
      positionTitle: app.positionTitle || positionTitle || '—',
      teamName: teamName ?? t('positions.noTeam'),
    })),
    [filtered, positionTitle, teamName, t],
  );

  const applicationSortOptions = useMemo(() => ({
    getSortValue: (item: ApplicationRow, column: string) => {
      if (column === 'selection') return selectedIds.has(item.id) ? 0 : 1;
      if (column === 'interviewStatus') return getInterviewSortKey(item.interviews);
      return getApplicationListSortValue(item, column);
    },
  }), [selectedIds]);

  const { sorted, sort, toggleSort } = useSortableTable(
    rowsWithTeam,
    queueSortDefaults,
    { ...applicationSortOptions, resetKey: queueView },
  );

  const handleQueueViewChange = useCallback((next: ApplicationQueueView) => {
    if (next === queueView) return;
    queueSlicesRef.current[queueView] = captureQueueSlice({
      page,
      pinnedStatus,
      sortColumn: sort.column ?? queueSortDefaults.column,
      sortDirection: (sort.direction ?? queueSortDefaults.direction) as 'asc' | 'desc',
      selectedIds,
    });
    const nextSlice = queueSlicesRef.current[next];
    setQueueSortDefaults({
      column: nextSlice.sortColumn,
      direction: nextSlice.sortDirection,
    });
    setQueueView(next);
    setPage(nextSlice.page);
    setSelectedIds(new Set(nextSlice.selectedIds ?? []));
    setPinnedStatus(nextSlice.pinnedStatus);
  }, [queueView, page, pinnedStatus, selectedIds, sort.column, sort.direction, queueSortDefaults.column, queueSortDefaults.direction]);

  const statusPinCycle = useMemo(
    () => getPresentStatusPinCycle(rowsWithTeam, hasProcessingCredits),
    [rowsWithTeam, hasProcessingCredits],
  );

  const promoteIds = useMemo(() => {
    if (!bulkBannerActive || processQueue.activeCount === 0) return undefined;
    const ids = new Set<string>();
    for (const id of processQueue.processingIds) {
      if (processQueue.isProcessing(id)) ids.add(id);
    }
    for (const id of processQueue.stoppingIds) {
      if (processQueue.isStopping(id)) ids.add(id);
    }
    return ids.size > 0 ? ids : undefined;
  }, [
    bulkBannerActive,
    processQueue.activeCount,
    processQueue.processingIds,
    processQueue.stoppingIds,
    processQueue.isProcessing,
    processQueue.isStopping,
  ]);

  const displaySorted = useMemo(
    () => buildApplicationDisplayOrder(sorted, { pinnedStatus, promoteIds, hasProcessingCredits }),
    [sorted, pinnedStatus, promoteIds, hasProcessingCredits],
  );

  const totalSorted = displaySorted.length;
  const totalPages = Math.max(1, Math.ceil(totalSorted / DEFAULT_LIST_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * DEFAULT_LIST_PAGE_SIZE;
    return displaySorted.slice(start, start + DEFAULT_LIST_PAGE_SIZE);
  }, [displaySorted, safePage]);

  const {
    clearSelection,
    selectAllOnPage,
    deselectAllOnPage,
    handleRowSelect,
  } = useShiftClickSelection(pageRows, initialSlice.selectedIds, {
    selectedIds,
    setSelectedIds,
  });

  const shiftKeyRef = useRef(false);

  const persistedListState = useMemo(() => {
    const currentSlice = captureQueueSlice({
      page,
      pinnedStatus,
      sortColumn: sort.column ?? queueSortDefaults.column,
      sortDirection: (sort.direction ?? queueSortDefaults.direction) as 'asc' | 'desc',
      selectedIds,
    });
    queueSlicesRef.current[queueView] = currentSlice;
    return {
      queueView,
      statusFilter,
      sourceFilter,
      search,
      listSortVersion: APPLICATIONS_LIST_SORT_VERSION,
      active: queueSlicesRef.current.active,
      archive: queueSlicesRef.current.archive,
    };
  }, [
    queueView,
    statusFilter,
    sourceFilter,
    search,
    page,
    pinnedStatus,
    sort.column,
    sort.direction,
    queueSortDefaults.column,
    queueSortDefaults.direction,
    selectedIds,
  ]);

  usePersistedListView(listPersistKey, persistedListState);

  const pruneSelectionQueueRef = useRef(queueView);
  useEffect(() => {
    if (pruneSelectionQueueRef.current !== queueView) {
      pruneSelectionQueueRef.current = queueView;
      return;
    }
    if (listApplications.length === 0) return;
    const valid = new Set(listApplications.map(a => a.id));
    setSelectedIds(prev => {
      const next = new Set([...prev].filter(id => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [listApplications, queueView, setSelectedIds]);

  useSkipInitialEffect(() => {
    setPage(1);
    setSearchParams((prev) => applyListPageToSearchParams(prev, 1), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset page only when list filters change
  }, [statusFilter, sourceFilter, search]);

  useEffect(() => {
    if (listLoading || totalSorted === 0) return;
    if (page > totalPages) {
      setPage(totalPages);
      setSearchParams((prev) => applyListPageToSearchParams(prev, totalPages), { replace: true });
    }
  }, [page, totalPages, listLoading, totalSorted, setSearchParams]);

  const cycleStatusPin = useCallback(() => {
    setPinnedStatus(prev => getNextStatusPin(prev, statusPinCycle));
  }, [statusPinCycle]);

  const handleColumnSort = useCallback((column: string) => {
    setPinnedStatus(null);
    toggleSort(column);
  }, [toggleSort]);

  useEffect(() => {
    if (pinnedStatus && !statusPinCycle.includes(pinnedStatus)) {
      setPinnedStatus(null);
    }
  }, [pinnedStatus, statusPinCycle]);

  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectNote, setBulkRejectNote] = useState('');
  const [bulkRejectSendEmail, setBulkRejectSendEmail] = useState(false);
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  const [bulkProcessConfirmOpen, setBulkProcessConfirmOpen] = useState(false);

  const handleBulkReject = useCallback(async () => {
    if (!guardWrite()) return;
    if (!companyId || !posId || selectedIds.size === 0) return;
    setBulkActionRunning(true);
    const ids = [...selectedIds];
    const results = await Promise.allSettled(
      ids.map(id => api.applications.reject(companyId, posId, id, bulkRejectNote || undefined, bulkRejectSendEmail)),
    );
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    if (ok > 0) toast.success(t('applications.bulkRejectSuccess', { count: ok }));
    if (fail > 0) toast.error(t('applications.bulkRejectPartial', { count: fail }));
    setBulkRejectOpen(false);
    setBulkRejectNote('');
    setBulkRejectSendEmail(false);
    if (onRefetch) await onRefetch();
    setBulkActionRunning(false);
  }, [guardWrite, companyId, posId, selectedIds, bulkRejectNote, bulkRejectSendEmail, onRefetch, t]);

  const selectedApps = useMemo(
    () => [...selectedIds]
      .map(id => listApplications.find(a => a.id === id))
      .filter((app): app is Application => !!app),
    [selectedIds, listApplications],
  );

  const scheduleArchiveInvalidation = useCallback(() => {
    if (!companyId) return;
    scheduleApplicationArchiveInvalidation(queryClient, companyId);
  }, [companyId, queryClient]);

  /** @deprecated Renamed to scheduleArchiveInvalidation — kept so stale hook deps cannot crash render. */
  const invalidateArchiveQueries = scheduleArchiveInvalidation;

  const handleBulkArchive = useCallback(async () => {
    if (!guardWrite()) return;
    if (!companyId || selectedIds.size === 0) return;
    setBulkActionRunning(true);
    try {
      const result = await api.applications.bulkArchive(companyId, toBulkArchivePayload(selectedApps));
      if (result.updated > 0) {
        applyBulkArchiveToCache(queryClient, companyId, selectedApps);
        toast.success(t('applications.bulkArchiveSuccess', { count: result.updated }));
      }
      if (result.errors > 0) toast.error(t('applications.bulkArchivePartial', { count: result.errors }));
      setSelectedIds(new Set());
      scheduleArchiveInvalidation();
    } finally {
      setBulkActionRunning(false);
    }
  }, [guardWrite, companyId, selectedIds.size, selectedApps, queryClient, scheduleArchiveInvalidation, t]);

  const handleBulkUnarchive = useCallback(async () => {
    if (!guardWrite()) return;
    if (!companyId || selectedIds.size === 0) return;
    setBulkActionRunning(true);
    try {
      const result = await api.applications.bulkUnarchive(companyId, toBulkArchivePayload(selectedApps));
      if (result.updated > 0) {
        applyBulkUnarchiveToCache(queryClient, companyId, selectedApps);
        toast.success(t('applications.bulkUnarchiveSuccess', { count: result.updated }));
      }
      if (result.errors > 0) toast.error(t('applications.bulkUnarchivePartial', { count: result.errors }));
      if (result.updated === 0 && result.errors > 0) {
        toast.error(t('applications.bulkUnarchiveUnavailable'));
      }
      setSelectedIds(new Set());
      scheduleArchiveInvalidation();
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      toast.error(msg || t('applications.bulkUnarchiveUnavailable'));
    } finally {
      setBulkActionRunning(false);
    }
  }, [
    guardWrite,
    companyId,
    selectedIds.size,
    selectedApps,
    queryClient,
    scheduleArchiveInvalidation,
    t,
  ]);

  const bulkSelectionBreakdown = useMemo(
    () => getBulkProcessBreakdown(selectedApps, hasProcessingCredits),
    [selectedApps, hasProcessingCredits],
  );

  const selectedProcessableCount = bulkProcessableCount(bulkSelectionBreakdown);
  const selectedStopCount = processQueue.countSelectedStopEligible(selectedApps);
  const bulkProcessButton = getBulkProcessButtonState(bulkSelectionBreakdown, hasProcessingCredits);

  const handleBulkStopProcessing = useCallback(() => {
    if (!guardWrite()) return;
    if (!companyId || selectedStopCount === 0) return;
    const stopTargets = selectedApps.filter((a) => processQueue.isExplicitStopEligible(a.id));
    void processQueue.runBulkAbortStuck(stopTargets, { mode: 'explicit' });
  }, [guardWrite, companyId, selectedStopCount, selectedApps, processQueue]);

  const handleBulkProcess = useCallback(() => {
    if (!guardWrite()) return;
    if (!companyId || selectedProcessableCount === 0 || !hasProcessingCredits) return;
    const processable = getSelectedProcessableApps(selectedIds, applications, hasProcessingCredits);

    setBulkProcessConfirmOpen(false);

    processQueue.prepareBulkProcess(processable);
    setPage(1);

    void (async () => {
      const result = await processQueue.runBulkProcess(processable, { skipPrepare: true });
      if (result?.skippedBilling > 0 && result.processed === 0) {
        setStatusFilter('PENDING_UPGRADE');
        setPage(1);
      }
      const unlocked = await api.applications.unlockDeferred(companyId);
      setDeferredCounts(
        reconcileDeferredCountsWithApplications(queryClient, companyId, hasProcessingCredits, {
          pendingUpgrade: unlocked.pendingUpgrade,
          readyToProcess: unlocked.readyToProcess,
          billingRetry: unlocked.billingRetry,
        }),
      );
      if (unlocked.unlocked > 0) {
        patchUnlockedDeferredApplications(queryClient, companyId);
      }
      await queryClient.invalidateQueries({ queryKey: ['applications', companyId] });
      emitBillingUsageChanged();
    })();
  }, [
    guardWrite,
    companyId,
    selectedProcessableCount,
    hasProcessingCredits,
    selectedIds,
    applications,
    processQueue,
    setDeferredCounts,
    queryClient,
  ]);

  const dimAwaitingUpgrade = (row: ApplicationRow) =>
    isApplicationAwaitingUpgrade(row, hasProcessingCredits) ? 'opacity-60' : undefined;

  const applicationRowClassName = (row: ApplicationRow) =>
    cn(
      isApplicationAwaitingUpgrade(row, hasProcessingCredits) && 'bg-amber-50/30 dark:bg-amber-950/10',
      isApplicationReadyToProcess(row, hasProcessingCredits) && 'bg-muted/20',
      selectedIds.has(row.id) && 'bg-primary/5',
    );

  const applicationColumns = useMemo((): ResponsiveTableColumn<ApplicationRow>[] => [
    {
      id: 'selection',
      label: '',
      priority: 'hidden',
      alwaysVisible: true,
      sortable: false,
      ...TABLE_COL.checkbox,
      renderHeader: () => (
        <SelectAllHeaderCheckbox
          pageRows={pageRows}
          selectedIds={selectedIds}
          selectAllOnPage={selectAllOnPage}
          deselectAllOnPage={deselectAllOnPage}
          clearSelection={clearSelection}
          ariaLabel={t('applications.bulkSelectAll')}
        />
      ),
      render: (row) => {
        const index = pageRows.findIndex((r) => r.id === row.id);
        return (
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-input accent-primary cursor-pointer block mx-auto"
            checked={selectedIds.has(row.id)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => { shiftKeyRef.current = e.shiftKey; }}
            onChange={() => { handleRowSelect(index, row.id, shiftKeyRef.current); }}
            aria-label={`Select ${row.candidateFirstName} ${row.candidateLastName}`}
          />
        );
      },
    },
    {
      id: 'candidateFirstName',
      label: t('applications.candidate'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.title,
      sortable: true,
      truncate: true,
      render: (row) => (
        <div className={cn('flex items-center gap-1.5 min-w-0', dimAwaitingUpgrade(row))}>
          {row.hasIdentityMismatch && (
            <IdentityMismatchBadge mismatch={row.identityMismatch} variant="icon" className="shrink-0" />
          )}
          <span
            className="font-medium truncate block min-w-0"
            title={`${row.candidateFirstName} ${row.candidateLastName}`}
          >
            {row.candidateFirstName} {row.candidateLastName}
          </span>
        </div>
      ),
    },
    {
      id: 'candidateEmail',
      label: t('applications.email'),
      priority: 'secondary',
      hideOn: 'mobile',
      ...TABLE_COL.text,
      truncate: true,
      sortable: true,
      defaultVisible: false,
      render: (row) => (
        <span
          className={cn('text-muted-foreground truncate block', dimAwaitingUpgrade(row))}
          title={row.candidateEmail}
        >
          {row.candidateEmail}
        </span>
      ),
    },
    {
      id: 'positionTitle',
      label: t('applications.position'),
      priority: 'secondary',
      ...TABLE_COL.subtitle,
      sortable: true,
      truncate: true,
      render: (row) => (
        <span
          className={cn('text-muted-foreground truncate block', dimAwaitingUpgrade(row))}
          title={row.positionTitle}
        >
          {row.positionTitle}
        </span>
      ),
    },
    {
      id: 'teamName',
      label: t('applications.team'),
      priority: 'tertiary',
      ...TABLE_COL.text,
      sortable: true,
      truncate: true,
      defaultVisible: false,
      render: (row) => (
        <span
          className={cn('text-muted-foreground truncate block', dimAwaitingUpgrade(row))}
          title={row.teamName}
        >
          {row.teamName}
        </span>
      ),
    },
    {
      id: 'channel',
      label: t('applications.source'),
      priority: 'tertiary',
      ...TABLE_COL.channel,
      sortable: true,
      render: (row) => (
        <div className={cn(dimAwaitingUpgrade(row))}>
          <ChannelBadge channel={row.channel} />
        </div>
      ),
    },
    {
      id: 'appliedAt',
      label: t('applications.applied'),
      priority: 'tertiary',
      hideOn: 'mobile',
      ...TABLE_COL.date,
      sortable: true,
      render: (row) => (
        <span
          className={cn('text-muted-foreground', dimAwaitingUpgrade(row))}
          title={dateTimeTooltip(row.appliedAt)}
        >
          {formatRelative(row.appliedAt)}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      label: t('applications.updated'),
      priority: 'tertiary',
      ...TABLE_COL.date,
      sortable: true,
      render: (row) => (
        <span
          className={cn('text-muted-foreground', dimAwaitingUpgrade(row))}
          title={dateTimeTooltip(row.updatedAt)}
        >
          {formatRelative(row.updatedAt)}
        </span>
      ),
    },
    {
      id: 'aiScore',
      label: t('applications.score'),
      priority: 'tertiary',
      sortable: true,
      ...TABLE_COL.score,
      render: (row) => (
        <div className={cn(dimAwaitingUpgrade(row))}>
          <ScoreBadge score={row.aiScore} />
        </div>
      ),
    },
    {
      id: 'state',
      label: t('applications.status'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.status,
      onHeaderClick: cycleStatusPin,
      headerPin: true,
      render: (row) => (
        <div className={cn(statusCellClass(), dimAwaitingUpgrade(row))}>
          <ApplicationRowProcessBadge
            state={row.state}
            stopping={processQueue.isStopping(row.id)}
            processing={processQueue.showsActiveProcessingBadge(row.id)}
            queued={processQueue.showsQueuedInBulkBadge(row.id)}
            stuck={processQueue.showsProcessingStuckBadge(row.id)}
            hasProcessingCredits={hasProcessingCredits}
          />
        </div>
      ),
    },
    {
      id: 'interviewStatus',
      label: t('applications.interview'),
      priority: 'tertiary',
      alwaysVisible: true,
      ...TABLE_COL.interview,
      sortable: true,
      render: (row) => (
        <div className={cn(dimAwaitingUpgrade(row))}>
          <InterviewSubstatusBadges interviews={row.interviews} />
        </div>
      ),
    },
  ], [
    t,
    selectedIds,
    pageRows,
    cycleStatusPin,
    clearSelection,
    selectAllOnPage,
    deselectAllOnPage,
    handleRowSelect,
    processQueue,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="mb-3 shrink-0 flex flex-wrap items-center gap-3">
        <ApplicationQueueToggle
          value={queueView}
          onChange={handleQueueViewChange}
          activeLabel={t('applications.queueActive')}
          archiveLabel={t('applications.queueArchive')}
          activeCount={positionActiveCount}
          archivedCount={positionArchivedCount}
        />
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder={t('applications.searchCandidates')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={activeListFilterClass(search.trim().length > 0, 'pl-9 h-9')}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={activeListFilterClass(statusFilter !== 'all', 'w-[180px] h-9')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className={activeListFilterClass(sourceFilter !== 'all', 'w-[180px] h-9')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('applications.allSources')}</SelectItem>
            {APPLICATION_SOURCES.map((source) => (
              <SelectItem key={source} value={source}>{source}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasActiveListFilters ? (
          <ClearListFiltersButton onClick={clearAllListFilters} label={t('applications.clearFilters')} />
        ) : null}
        {!isArchiveView && onAddApplication && (
          <Button
            size="sm"
            className="gap-1.5 h-9 ml-auto"
            disabled={writeBlocked}
            onClick={() => {
              if (!guardWrite()) return;
              onAddApplication();
            }}
          >
            <UserPlus className="h-3.5 w-3.5" />
            {t('manualApply.cta')}
          </Button>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-3 shrink-0 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm">
          <span className="font-medium">
            {t('applications.bulkSelectedCount', { count: selectedIds.size })}
          </span>
          <span className="flex-1" />
          {isArchiveView ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => void handleBulkUnarchive()}
              disabled={bulkActionRunning || writeBlocked}
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              {t('applications.bulkUnarchive')}
            </Button>
          ) : (
            <>
              {selectedStopCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 border-amber-600/40 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  onClick={handleBulkStopProcessing}
                  disabled={writeBlocked}
                  title={t('applications.bulkStopTooltip')}
                >
                  <Square className="h-3.5 w-3.5" />
                  {t('applications.bulkStop')}
                  <span className="text-xs text-muted-foreground tabular-nums">
                    ({selectedStopCount})
                  </span>
                </Button>
              )}
              {bulkProcessButton.show && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:pointer-events-auto"
                  onClick={() => {
                    if (!guardWrite()) return;
                    setBulkProcessConfirmOpen(true);
                  }}
                  disabled={!bulkProcessButton.enabled || writeBlocked}
                  title={bulkProcessButton.disabledTitleKey ? t(bulkProcessButton.disabledTitleKey) : undefined}
                >
                  <Zap className="h-3.5 w-3.5" />
                  {t('applications.bulkProcess')}
                  {processQueue.activeCount > 0 ? (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      ({processQueue.activeCount})
                    </span>
                  ) : null}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => void handleBulkArchive()}
                disabled={bulkActionRunning || writeBlocked}
              >
                <Archive className="h-3.5 w-3.5" />
                {t('applications.bulkArchive')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => {
                  if (!guardWrite()) return;
                  setBulkRejectOpen(true);
                }}
                disabled={bulkActionRunning || writeBlocked}
              >
                <XCircle className="h-3.5 w-3.5" />
                {t('applications.bulkReject')}
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" className="h-8" onClick={clearSelection} disabled={bulkActionRunning}>
            {t('applications.bulkClear')}
          </Button>
        </div>
      )}
      <ListTablePanel
        scrollableBody
        fillViewport
        className="flex-1 min-h-0"
        showFooter={totalSorted > 0}
        footer={
          <ListPagination
            page={safePage}
            totalPages={totalPages}
            totalItems={totalSorted}
            onPageChange={handlePageChange}
            disabled={listLoading}
          />
        }
      >
        <ResponsiveTable<ApplicationRow>
          scrollBodyOnly
          className="h-full min-h-0"
          data={pageRows}
          columns={applicationColumns}
          getRowKey={(app) => app.id}
          onRowClick={openApplicationDetail}
          sort={sort}
          onSortChange={handleColumnSort}
          storageKey="position-applications-v2"
          isLoading={listLoading && pageRows.length === 0}
          rowClassName={applicationRowClassName}
          emptyState={(
            <div className="py-12 text-center text-muted-foreground text-sm space-y-2">
              <p>
                {filtersHideAllRows
                  ? t('applications.noApplicationsForFilter')
                  : t('applications.noApplications')}
              </p>
              {filtersHideAllRows ? (
                <Button type="button" variant="outline" size="sm" onClick={clearAllListFilters}>
                  {t('applications.clearFilters')}
                </Button>
              ) : null}
            </div>
          )}
          ariaLabel={t('applications.candidate')}
          fixedPageSize={undefined}
        />
      </ListTablePanel>

    <Dialog open={bulkRejectOpen} onOpenChange={setBulkRejectOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('applications.bulkRejectTitle', { count: selectedIds.size })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('applications.bulkRejectDesc')}</p>
        <Textarea
          placeholder={t('appDetail.rejectNote')}
          value={bulkRejectNote}
          onChange={(e) => setBulkRejectNote(e.target.value)}
          rows={3}
        />
        <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-primary"
            checked={bulkRejectSendEmail}
            onChange={(e) => setBulkRejectSendEmail(e.target.checked)}
          />
          <span>
            {t('appDetail.rejectSendEmail')}
            <span className="block text-[11px] text-muted-foreground/80">
              {t('appDetail.rejectSendEmailHint')}
            </span>
          </span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => setBulkRejectOpen(false)} disabled={bulkActionRunning}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => { void handleBulkReject(); }}
            disabled={bulkActionRunning}
          >
            {bulkActionRunning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {t('applications.bulkRejectConfirm', { count: selectedIds.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ProcessPendingConfirmDialog
      open={bulkProcessConfirmOpen}
      onOpenChange={setBulkProcessConfirmOpen}
      breakdown={bulkSelectionBreakdown}
      busy={false}
      variant="bulk"
      onConfirm={handleBulkProcess}
    />
    </div>
  );
};

export default PositionDetailPage;
